import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { buildSchemas, resolveSeo } from "../seo/seoConfig";
import { siteConfig } from "../siteConfig";

const setMetaTag = (selector, attributes) => {
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement("meta");
    document.head.appendChild(tag);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    tag.setAttribute(key, value);
  });
};

const setLinkTag = (selector, attributes) => {
  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement("link");
    document.head.appendChild(tag);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    tag.setAttribute(key, value);
  });
};

const Seo = () => {
  const location = useLocation();

  useEffect(() => {
    const seo = resolveSeo(location.pathname);

    document.title = seo.title;

    setMetaTag('meta[name="description"]', {
      name: "description",
      content: seo.description,
    });
    setMetaTag('meta[name="keywords"]', {
      name: "keywords",
      content: seo.keywords,
    });
    setMetaTag('meta[name="robots"]', {
      name: "robots",
      content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    });
    setMetaTag('meta[name="author"]', {
      name: "author",
      content: siteConfig.siteName,
    });
    setMetaTag('meta[property="og:title"]', {
      property: "og:title",
      content: seo.ogTitle || seo.title,
    });
    setMetaTag('meta[property="og:description"]', {
      property: "og:description",
      content: seo.ogDescription || seo.description,
    });
    setMetaTag('meta[property="og:image"]', {
      property: "og:image",
      content: seo.image,
    });
    setMetaTag('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: seo.imageAlt,
    });
    setMetaTag('meta[property="og:url"]', {
      property: "og:url",
      content: seo.canonical,
    });
    setMetaTag('meta[property="og:type"]', {
      property: "og:type",
      content: seo.ogType || "website",
    });
    setMetaTag('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: siteConfig.siteName,
    });
    setMetaTag('meta[property="og:locale"]', {
      property: "og:locale",
      content: "en_IN",
    });
    setMetaTag('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    setMetaTag('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: seo.twitterTitle || seo.ogTitle || seo.title,
    });
    setMetaTag('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: seo.twitterDescription || seo.ogDescription || seo.description,
    });
    setMetaTag('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: seo.image,
    });
    setMetaTag('meta[name="twitter:image:alt"]', {
      name: "twitter:image:alt",
      content: seo.imageAlt,
    });
    setLinkTag('link[rel="canonical"]', {
      rel: "canonical",
      href: seo.canonical,
    });
    setLinkTag('link[rel="alternate"][hreflang="en-IN"]', {
      rel: "alternate",
      hreflang: "en-IN",
      href: seo.canonical,
    });
    setLinkTag('link[rel="alternate"][hreflang="x-default"]', {
      rel: "alternate",
      hreflang: "x-default",
      href: seo.canonical,
    });

    document.head
      .querySelectorAll('script[data-managed-seo="true"]')
      .forEach((node) => node.remove());

    buildSchemas(location.pathname, seo).forEach((schema) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-managed-seo", "true");
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("config", siteConfig.googleAnalyticsId, {
        page_title: seo.title,
        page_path: location.pathname,
        page_location: window.location.href,
      });
    }
  }, [location.pathname]);

  return null;
};

export default Seo;
